import { useTranslation } from 'react-i18next';
import { Alert, TextV2 } from '@insights-ltd/design-library/components';
import { Link, List, ListItem, styled } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Link as RouterLink } from 'react-router';
import { getChapterKey } from 'utils/getChapterKey';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';

type PurchaseSummaryLine = {
  chapterName: string;
  unitCost: number;
  count: number;
};

type PurchaseSummaryProps = {
  lines: PurchaseSummaryLine[];
  contributorsCount?: number;
  unitBalance: number;
  totalUnitCost: number;
  invalidTransaction?: boolean;
  newContributors?: number;
  showNewContributors?: boolean;
  variant?: 'default' | 'dfc';
};

const StyledDivRow = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  '> :last-child': {
    marginLeft: 'auto',
  },
});

const StyledTransactionBox = styled('div', {
  shouldForwardProp: (prop) => prop !== 'invalidTransaction',
})<{ invalidTransaction: boolean }>(({ invalidTransaction, theme }) => ({
  marginBottom: theme.spacing(spacingSizeMap.XS),
  padding: `${theme.spacing(spacingSizeMap.XS)} ${theme.spacing(
    spacingSizeMap.S,
  )}`,
  background: theme.palette.primary.light,
  borderRadius: '4px',
  border: invalidTransaction ? `solid 1px ${theme.palette.error.main}` : 'none',
}));

const StyledSummaryBox = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(spacingSizeMap.S),
}));

const StyledSummaryRow = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  '> :first-of-type': {
    width: '25px',
  },
  '> :last-child': {
    marginLeft: 'auto',
  },
});

const StyledSpan = styled('span')(({ theme }) => ({
  overflow: 'balance',
  width: ' 70%',
  color: theme.palette.grey[600],
}));

const PurchaseSummary = ({
  lines,
  unitBalance,
  totalUnitCost,
  invalidTransaction = false,
  contributorsCount = 0,
  showNewContributors = false,
  newContributors,
  variant = 'default',
}: PurchaseSummaryProps) => {
  const { t } = useTranslation();

  const postTransBalance = unitBalance - totalUnitCost;
  const summaryUnitsLabel = t(
    'ui.event-management.events.assign-chapters.summary-box.units',
  );

  const getChapterTranslation = (chapterName: string) => {
    if (variant === 'dfc') {
      return t(
        'ui.event-management.events.purchase-profiles.dfc.pre.purchase.profile.count',
      );
    }
    return t(getChapterKey(chapterName));
  };

  return (
    <Grid container spacing={2} sx={{ flexDirection: 'column' }}>
      {invalidTransaction && (
        <Grid>
          <Alert
            severity="error"
            sx={(theme) => ({
              alignItems: 'center',
              padding: '0',
              svg: { fill: theme.palette.error.main },
            })}
          >
            <TextV2
              component="span"
              sx={{ color: 'inherit', fontWeight: 'inherit' }}
            >
              {t('ui.event-management.events.purchase-profiles.error-balance')}{' '}
              <Link component={RouterLink} to="/account" color="textPrimary">
                <TextV2 component="span" color="primary">
                  {t(
                    'ui.event-management.events.purchase-profiles.error-balance-action-link',
                  )}
                </TextV2>
              </Link>
            </TextV2>
          </Alert>
        </Grid>
      )}

      <Grid>
        <StyledTransactionBox invalidTransaction={invalidTransaction}>
          <StyledDivRow>
            <TextV2 component="span" color="textSecondary">
              {t('ui.event-management.events.purchase-profiles.unit-balance')}
            </TextV2>
            <TextV2 component="span" variant="bodyBold" color="textSecondary">
              {unitBalance}
            </TextV2>
          </StyledDivRow>

          <StyledDivRow>
            <TextV2
              component="span"
              color={invalidTransaction ? 'error' : 'textSecondary'}
            >
              {t(
                'ui.event-management.events.purchase-profiles.transaction-total',
              )}
            </TextV2>
            <TextV2
              component="span"
              variant="bodyBold"
              color={invalidTransaction ? 'error' : 'textSecondary'}
            >
              {totalUnitCost}
            </TextV2>
          </StyledDivRow>

          {!invalidTransaction && (
            <StyledDivRow>
              <TextV2 component="span" color="textSecondary">
                {t(
                  'ui.event-management.events.purchase-profiles.post-transaction-balance',
                )}
              </TextV2>
              <TextV2 component="span" variant="bodyBold" color="textSecondary">
                {postTransBalance}
              </TextV2>
            </StyledDivRow>
          )}
        </StyledTransactionBox>
      </Grid>

      <Grid>
        <StyledSummaryBox>
          <TextV2 variant="bodyBold" color="textPrimary">
            {t('ui.event-management.events.purchase-profiles.summary-title')}:
          </TextV2>

          <List disablePadding>
            {lines.map(({ chapterName, count, unitCost }) => {
              const titleKey = getChapterKey(chapterName);
              const sumChapterUnits = count * unitCost;
              return (
                <ListItem dense disableGutters key={titleKey}>
                  <StyledSummaryRow>
                    <TextV2 component="span" color="textSecondary">
                      {count}x
                    </TextV2>
                    <TextV2 component="span" color="textSecondary">
                      {getChapterTranslation(chapterName)}
                    </TextV2>
                    <TextV2 component="span" color="textSecondary">
                      {`${sumChapterUnits} ${summaryUnitsLabel}`}
                    </TextV2>
                  </StyledSummaryRow>
                </ListItem>
              );
            })}

            {showNewContributors && newContributors && (
              <ListItem disableGutters key="total-contributors">
                <StyledSummaryRow>
                  <TextV2 component="span" color="textSecondary">
                    {contributorsCount}x
                  </TextV2>
                  <StyledSpan>
                    {t(
                      'ui.event-management.events.purchase-profiles.dfc.pre.purchase.contributor.count',
                    )}
                  </StyledSpan>
                  <TextV2 component="span" color="textSecondary">
                    {`0 ${summaryUnitsLabel}`}
                  </TextV2>
                </StyledSummaryRow>
              </ListItem>
            )}

            <ListItem dense disableGutters key="total-line">
              <StyledDivRow>
                <TextV2 component="p" variant="h4" color="textPrimary">
                  {t(
                    'ui.event-management.events.purchase-profiles.basket-total-label',
                  )}
                  :
                </TextV2>
                <TextV2
                  component="p"
                  data-testid="totalTransactionUnits"
                  variant="h4"
                  color="textPrimary"
                >
                  {totalUnitCost} {summaryUnitsLabel}
                </TextV2>
              </StyledDivRow>
            </ListItem>
          </List>
        </StyledSummaryBox>
      </Grid>
    </Grid>
  );
};

export default PurchaseSummary;
